import axios from 'axios';
import { API_BASE } from '../lib/apiBase';

export const API = API_BASE;

// Auth is handled via an httpOnly cookie set by the backend on login.
// The token is never exposed to JS/localStorage; the browser sends it
// automatically because every request uses withCredentials.
export const api = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      const p = window.location.pathname;
      if (p.startsWith('/admin') && !p.endsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

// ---- Auth ----
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }).then((r) => r.data),
  register: (body) => api.post('/auth/register', body).then((r) => r.data),
  registerPartner: (body) => api.post('/auth/register-partner', body).then((r) => r.data),
  logout: () => api.post('/auth/logout').then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
};

// ---- Partner ----
export const partnerApi = {
  me: () => api.get('/partner/me').then((r) => r.data),
  updateProfile: (body) => api.put('/partner/profile', body).then((r) => r.data),
  selectPlan: (plan) => api.post('/partner/subscription/select', { plan }).then((r) => r.data),
};

// tenant_id query param helper (super admin scoping)
const tq = (tenantId) => (tenantId ? `?tenant_id=${tenantId}` : '');

export const adminApi = {
  // tenants
  listTenants: () => api.get('/tenants').then((r) => r.data),
  getTenant: (id) => api.get(`/tenants/${id}`).then((r) => r.data),
  createTenant: (body) => api.post('/tenants', body).then((r) => r.data),
  updateTenant: (id, body) => api.put(`/tenants/${id}`, body).then((r) => r.data),
  deleteTenant: (id) => api.delete(`/tenants/${id}`),
  // users
  listUsers: () => api.get('/users').then((r) => r.data),
  createUser: (body) => api.post('/users', body).then((r) => r.data),
  updateUser: (id, body) => api.put(`/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  // content entities
  listEntities: (entity, tid) => api.get(`/content/${entity}${tq(tid)}`).then((r) => r.data),
  createEntity: (entity, body, tid) => api.post(`/content/${entity}${tq(tid)}`, body).then((r) => r.data),
  updateEntity: (entity, id, body, tid) => api.put(`/content/${entity}/${id}${tq(tid)}`, body).then((r) => r.data),
  deleteEntity: (entity, id, tid) => api.delete(`/content/${entity}/${id}${tq(tid)}`),
  reorder: (entity, ids, tid) => api.post(`/content/${entity}/reorder${tq(tid)}`, { ordered_ids: ids }).then((r) => r.data),
  // pages
  getPage: (key, tid) => api.get(`/pages/${key}${tq(tid)}`).then((r) => r.data),
  putPage: (key, data, tid) => api.put(`/pages/${key}${tq(tid)}`, { data }).then((r) => r.data),
  // media
  listMedia: (tid) => api.get(`/media${tq(tid)}`).then((r) => r.data),
  uploadMedia: (file, tid) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/media${tq(tid)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  deleteMedia: (id, tid) => api.delete(`/media/${id}${tq(tid)}`),
  // property QR
  regeneratePropertyQr: (tid) => api.post(`/qr/property/regenerate${tq(tid)}`).then((r) => r.data),
  // guest access (token + PIN)
  getGuestAccess: (id) => api.get(`/tenants/${id}/guest-access`).then((r) => r.data),
  updateGuestPin: (id, pin) => api.put(`/tenants/${id}/guest-access/pin`, { pin }).then((r) => r.data),
  regenerateGuestToken: (id) => api.post(`/tenants/${id}/guest-access/regenerate`).then((r) => r.data),
};

// ---- QR scan analytics ----
export const scanApi = {
  stats: (tid) => api.get(`/scan/stats${tq(tid)}`).then((r) => r.data),
};

// ---- Super Admin monitoring ----
export const monitorApi = {
  overview: () => api.get('/admin/monitor/overview').then((r) => r.data),
  logs: (params = {}) => api.get('/admin/monitor/logs', { params }).then((r) => r.data),
  clearCache: (module) => api.post(`/admin/monitor/cache/clear${module ? `?module=${module}` : ''}`).then((r) => r.data),
};

// ---- Host custom content (Content Engine) ----
export const customApi = {
  list: (module, tid) => api.get(`/custom/${module}${tq(tid)}`).then((r) => r.data),
  create: (module, body, tid) => api.post(`/custom/${module}${tq(tid)}`, body).then((r) => r.data),
  update: (module, id, body, tid) => api.put(`/custom/${module}/${id}${tq(tid)}`, body).then((r) => r.data),
  remove: (module, id, tid) => api.delete(`/custom/${module}/${id}${tq(tid)}`),
};

// ---- Catalog selection (host: show/hide/reorder resolved catalog) ----
export const catalogSelectionApi = {
  list: (entity, tid) => api.get(`/catalog-selection/${entity}${tq(tid)}`).then((r) => r.data),
  save: (entity, body, tid) => api.put(`/catalog-selection/${entity}${tq(tid)}`, body).then((r) => r.data),
};

// ---- Global Catalog (super admin) ----
export const catalogApi = {
  getConfig: () => api.get('/catalog/config').then((r) => r.data),
  updateConfig: (radii) => api.put('/catalog/config', { radii }).then((r) => r.data),
  list: (entity, params = {}) => api.get(`/catalog/${entity}`, { params }).then((r) => r.data),
  create: (entity, body) => api.post(`/catalog/${entity}`, body).then((r) => r.data),
  update: (entity, id, body) => api.put(`/catalog/${entity}/${id}`, body).then((r) => r.data),
  remove: (entity, id) => api.delete(`/catalog/${entity}/${id}`),
};

// ---- Subscription ----
export const subApi = {
  get: (tid) => api.get(`/subscription${tid ? `?tenant_id=${tid}` : ''}`).then((r) => r.data),
  select: (plan, tid) => api.post(`/subscription/select${tid ? `?tenant_id=${tid}` : ''}`, { plan }).then((r) => r.data),
};
